import { resolveSessionPopPublicKey } from "./cmmdSessionPopKey";
import { resolvePrimaryEnvironmentHttpUrl } from "./target";

const COMMAND_OAUTH_CLIENT_ID = "cmmd-forge";
const COMMAND_OAUTH_GRANT_TYPE = "urn:ietf:params:oauth:grant-type:device_code";

export type CommandDeviceAuthorization = {
  readonly device_code: string;
  readonly user_code: string;
  readonly verification_uri: string;
  readonly verification_uri_complete: string;
  readonly expires_in: number;
  readonly interval: number;
};

export type CommandDeviceTokenSuccess = {
  readonly access_token: string;
  readonly refresh_token?: string | null;
  readonly mcp_token?: string | null;
  readonly expires_in?: number | null;
  readonly user?: {
    readonly id?: number | string;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly email: string;
    readonly username: string;
    readonly avatarUrl?: string | null;
  };
};

export type CommandDeviceOAuthEndpointOptions = {
  readonly httpBaseUrl?: string;
  /**
   * Resolves this browser's proof-of-possession public key for session binding
   * (forge#3517). Null (the default while the rollout gate is off) keeps the
   * poll payload byte-identical to the unbound bearer flow. With the gate on,
   * a key storage failure propagates and fails the poll closed. Injectable so
   * tests can pin both lane variants.
   */
  readonly resolvePopPublicKey?: () => Promise<string | null>;
};

type CommandDeviceTokenError = {
  readonly error?: string;
  readonly message?: string;
  readonly error_description?: string;
  readonly code?: string;
};

function commandDeviceTokenErrorMessage(
  response: Response,
  payload: Partial<CommandDeviceTokenError>,
): string {
  if (payload.error === "access_denied") {
    return "CMMD OAuth was denied. Try again and approve the request.";
  }

  if (payload.error === "expired_token") {
    return "CMMD OAuth timed out before approval completed.";
  }

  const detail = payload.error_description ?? payload.message ?? payload.error ?? payload.code;
  if (detail && detail.trim().length > 0) {
    return `CMMD OAuth token exchange failed: ${detail.trim()}`;
  }

  return `CMMD OAuth token exchange failed (${response.status}).`;
}

function resolveCommandDeviceOAuthUrl(
  pathname: string,
  options?: CommandDeviceOAuthEndpointOptions,
): string {
  if (!options?.httpBaseUrl) {
    return resolvePrimaryEnvironmentHttpUrl(pathname);
  }

  const url = new URL(options.httpBaseUrl);
  url.pathname = pathname;
  url.search = "";
  url.hash = "";
  return url.toString();
}

export async function requestCommandDeviceAuthorization(
  signal: AbortSignal,
  options?: CommandDeviceOAuthEndpointOptions,
): Promise<CommandDeviceAuthorization> {
  const endpoint = resolveCommandDeviceOAuthUrl("/api/auth/cmmd-oauth/device", options);

  let response: Response;
  try {
    response = await fetch(endpoint, {
      body: JSON.stringify({
        client_id: COMMAND_OAUTH_CLIENT_ID,
        scope: "read write admin",
      }),
      headers: {
        "content-type": "application/json",
      },
      credentials: "include",
      method: "POST",
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new Error(
      "Could not connect to the Forge server to start CMMD login. Make sure the server is running.",
      { cause: err },
    );
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      readonly message?: string;
      readonly error?: string;
    };
    throw new Error(
      payload.message ?? payload.error ?? `CMMD login failed to start (${response.status}).`,
    );
  }

  return (await response.json()) as CommandDeviceAuthorization;
}

export async function pollCommandAccessToken(
  authorization: CommandDeviceAuthorization,
  signal: AbortSignal,
  options?: CommandDeviceOAuthEndpointOptions,
): Promise<CommandDeviceTokenSuccess> {
  const endpoint = resolveCommandDeviceOAuthUrl("/api/auth/cmmd-oauth/token", options);
  const intervalMs = Math.max(1, authorization.interval) * 1_000;
  const deadline = Date.now() + Math.max(30, authorization.expires_in) * 1_000;
  let delayMs = intervalMs;

  // Resolved once per poll loop: the key is stable for the browser session and
  // CMMD binds whatever key the granting poll presented.
  const body = await buildDeviceTokenRequestBody(authorization, options);

  while (Date.now() < deadline) {
    const response = await fetch(endpoint, {
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
      },
      credentials: "include",
      method: "POST",
      signal,
    });

    const payload = (await response.json().catch(() => ({}))) as Partial<
      CommandDeviceTokenSuccess & CommandDeviceTokenError
    >;

    if (payload.error === "authorization_pending") {
      await waitForDelay(delayMs, signal);
      continue;
    }

    if (payload.error === "slow_down") {
      delayMs = Math.ceil(delayMs * 1.5);
      await waitForDelay(delayMs, signal);
      continue;
    }

    if (payload.error === "rate_limit_exceeded" || response.status === 429) {
      delayMs = Math.max(delayMs, 60_000);
      await waitForDelay(delayMs, signal);
      continue;
    }

    if (response.ok) {
      if (typeof payload.access_token === "string" && payload.access_token.length > 0) {
        return payload as CommandDeviceTokenSuccess;
      }
      throw new Error("CMMD OAuth completed without returning an access token.");
    }

    throw new Error(commandDeviceTokenErrorMessage(response, payload));
  }

  throw new Error("CMMD OAuth timed out before approval completed.");
}

/**
 * The poll body is constant across attempts. The proof-of-possession key is
 * attached only when the browser resolved one; CMMD binds the session to
 * whichever key the granting poll presented (forge#3517).
 */
async function buildDeviceTokenRequestBody(
  authorization: CommandDeviceAuthorization,
  options?: CommandDeviceOAuthEndpointOptions,
): Promise<Record<string, string>> {
  const popPublicKey = await (options?.resolvePopPublicKey ?? resolveSessionPopPublicKey)();
  return {
    grant_type: COMMAND_OAUTH_GRANT_TYPE,
    device_code: authorization.device_code,
    client_id: COMMAND_OAUTH_CLIENT_ID,
    ...(popPublicKey ? { pop_public_key: popPublicKey } : {}),
  };
}

function waitForDelay(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Request aborted.", "AbortError"));
      return;
    }

    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);

    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(new DOMException("Request aborted.", "AbortError"));
    };

    signal.addEventListener("abort", onAbort);
  });
}
